/**
 * Driver API Service Layer
 *
 * Handles all driver-related API calls with data transformation
 * between frontend and backend formats.
 */

import api from './api';

// Backend server base URL (without /api) for static file URLs
const BACKEND_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:4000/api').replace(/\/api$/, '');

/** Convert a relative upload path to a full URL */
function toFileUrl(path: string | null | undefined): string | undefined {
  if (!path) return undefined;
  if (path.startsWith('http')) return path;
  return `${BACKEND_BASE}${path}`;
}

// ==================== Backend Types ====================

export interface BackendDriver {
  id: number;
  user_id?: number;
  license_number: string;
  full_name?: string;
  phone?: string;
  email?: string;
  avatar_url?: string;
  driver_type?: string;
  company?: string;
  status?: string;
  rating?: number;
  is_available?: boolean;
  current_location?: string;
  /** City the driver's last GPS fix resolved to, plus when that fix arrived. */
  current_city?: string;
  current_state?: string;
  location_updated_at?: string;
  truck_id?: number;
  truck_plate?: string;
  truck_type?: string;
  truck_status?: string;
  owned_trucks?: Array<{ id: number; license_plate: string }>;
  active_tasks?: number;
  today_tasks?: number;
  hours_on_duty?: number;
  tasks?: Array<{ id: number; task_type: string; status: string; appointment_time: string | null }>;
  // Extended fields
  license_expiration?: string;
  cdl_class?: string;
  cdl_state?: string;
  cdl_issue_date?: string;
  cdl_restrictions?: string[];
  endorsements?: string[];
  cdl_photo_url?: string;
  medical_card_number?: string;
  dot_medical_expiration?: string;
  medical_card_photo_url?: string;
  twic_card_number?: string;
  twic_expiration?: string;
  twic_card_photo_url?: string;
  emodal_id?: string;
  pier_pass_id?: string;
  port_access_expiration?: string;
  insurance_provider?: string;
  insurance_policy_number?: string;
  insurance_expiration?: string;
  insurance_types?: string[];
  insurance_document_url?: string;
  drug_test_status?: string;
  last_drug_test?: string;
  drug_test_document_url?: string;
  employment_type?: string;
  work_status?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  language_preference?: string;
  preferred_port_yard?: string;
  task_preferences?: string[];
  app_login_enabled?: boolean;
  accidents?: number;
  violations?: number;
  safety_score?: number;
  last_accident_date?: string;
  last_violation_date?: string;
  dispatcher_note?: string;
  admin_note?: string;
  updated_at?: string;
}

interface DriverListResponse {
  data: BackendDriver[];
  total: number;
  limit: number;
  offset: number;
}

interface DriverListParams {
  limit?: number;
  offset?: number;
  status?: string;
  is_available?: boolean;
  driver_type?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

// ==================== Frontend Types ====================

export interface Driver {
  id: string;
  name: string;
  status: 'on-duty' | 'off-duty' | 'on-leave' | 'on-task';
  assignedTruck: string;
  plateLicense: string;
  truckNumber: string;
  truckType: string;
  truckStatus: string;
  ownedTrucks?: Array<{ id: string; licensePlate: string }>;
  company: string;
  businessName: string;
  ein: string;
  businessType: string;
  businessAddress: string;
  phone: string;
  email: string;
  avatar: string;
  driverId: string;
  employmentType: 'company-driver' | 'owner-operator' | 'contractor';
  workStatus: 'on-duty' | 'off-duty' | 'suspended';
  emergencyContact: {
    name: string;
    phone: string;
  };
  languagePreference: string;
  preferredPortYard: string;
  taskPreferences: string[];
  appLoginEnabled: boolean;
  // CDL Information
  licenseNumber: string;
  licenseExpiration: string;
  cdlClass: string;
  cdlState: string;
  cdlIssueDate: string;
  cdlRestrictions: string[];
  endorsements: string[];
  cdlPhoto?: string;
  // Medical Card
  medicalCardNumber: string;
  dotMedicalExpiration: string;
  medicalCardPhoto?: string;
  // TWIC Card
  twicCardNumber: string;
  twicExpiration: string;
  twicCardPhoto?: string;
  // Port Access
  eModalId: string;
  pierPassId: string;
  portAccessExpiration: string;
  // Insurance
  insuranceProvider: string;
  insurancePolicyNumber: string;
  insuranceExpiration: string;
  insuranceTypes: string[];
  insuranceDocument?: string;
  // Drug Testing
  drugTestStatus: 'pending' | 'passed' | 'failed';
  lastDrugTest: string;
  drugTestDocument?: string;
  // Safety Records
  accidents: number;
  violations: number;
  safetyScore: number;
  lastAccidentDate: string;
  lastViolationDate: string;
  // Notes
  dispatcherNote: string;
  adminNote: string;
  // Performance (loaded separately)
  rating?: number;
  // Card display fields
  location?: string;
  /** Age of the GPS fix `location` was derived from — a city with no time behind
   *  it can be days old and would read as "the driver is there now". */
  locationUpdatedAt?: string;
  activeTasks?: number;
  todayTasks?: number;
  hoursOnDuty?: number;
  tasks?: Array<{ id: number; taskType: string; status: string; appointmentTime: string | null }>;
  userId?: number;
}

export interface DriverNote {
  id: string;
  driverId: string;
  content: string;
  author: string;
  authorRole: 'dispatcher' | 'admin' | 'manager' | 'supervisor';
  noteType: 'internal' | 'shared';
  priority: 'normal' | 'high' | 'urgent';
  mentions: string[];
  tags?: string[];
  isEdited?: boolean;
  createdAt: string;
  updatedAt?: string;
}

interface DriverDocument {
  id: string;
  driverId: string;
  documentType: string;
  fileName: string;
  fileUrl: string;
  expirationDate?: string;
  documentStatus: 'valid' | 'expiring' | 'expired';
  reminderEnabled: boolean;
  notes?: string;
  createdAt: string;
}

interface DriverPerformance {
  deliveries: number;
  onTimeRate: number;
  averageWaitTime: number;
  taskAcceptanceRate: number;
  taskRejectionRate: number;
}

// ==================== Status Mapping ====================

const statusToFrontend: Record<string, Driver['status']> = {
  'on-duty': 'on-duty',
  'off-duty': 'off-duty',
  'on-leave': 'on-leave',
  'on-task': 'on-task',
  // Legacy mappings for old data
  'Available': 'on-duty',
  'Busy': 'on-task',
  'On Route': 'on-task',
  'On Break': 'off-duty',
  'On': 'on-duty',
  'Off': 'off-duty',
};

const statusToBackend: Record<Driver['status'], string> = {
  'on-duty': 'on-duty',
  'off-duty': 'off-duty',
  'on-leave': 'on-leave',
  'on-task': 'on-task',
};

// ==================== Transform Functions ====================

/** Normalize ISO timestamps (e.g. "2027-12-31T08:00:00.000Z") to yyyy-MM-dd for <input type="date"> */
function toDateInput(val: string | undefined | null): string {
  if (!val) return '';
  // Already yyyy-MM-dd
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
  const d = new Date(val);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().split('T')[0];
}

function transformFromBackend(data: BackendDriver): Driver {
  return {
    id: String(data.id),
    name: data.full_name || '',
    status: statusToFrontend[data.status || 'off-duty'] || 'off-duty',
    assignedTruck: data.truck_id ? String(data.truck_id) : 'unassigned',
    plateLicense: data.truck_plate || '',
    truckNumber: data.truck_id ? `T-${String(data.truck_id).padStart(4, '0')}` : '',
    truckType: data.truck_type || '',
    truckStatus: data.truck_status || '',
    ownedTrucks: data.owned_trucks?.map(t => ({ id: String(t.id), licensePlate: t.license_plate })),
    company: data.company || '',
    businessName: data.business_name || '',
    ein: data.ein || '',
    businessType: data.business_type || '',
    businessAddress: data.business_address || '',
    phone: data.phone || '',
    email: data.email || '',
    avatar: toFileUrl(data.avatar_url) || '',
    driverId: `DRV${String(data.id).padStart(3, '0')}`,
    employmentType: (data.driver_type || 'company-driver') as Driver['employmentType'],
    workStatus: (data.work_status || 'off-duty') as Driver['workStatus'],
    emergencyContact: {
      name: data.emergency_contact_name || '',
      phone: data.emergency_contact_phone || '',
    },
    languagePreference: data.language_preference || 'en',
    preferredPortYard: data.preferred_port_yard || 'any',
    taskPreferences: data.task_preferences || [],
    appLoginEnabled: data.app_login_enabled !== false,
    // CDL Information
    licenseNumber: data.license_number || '',
    licenseExpiration: toDateInput(data.license_expiration),
    cdlClass: data.cdl_class || 'A',
    cdlState: data.cdl_state || '',
    cdlIssueDate: toDateInput(data.cdl_issue_date),
    cdlRestrictions: data.cdl_restrictions || [],
    endorsements: data.endorsements || [],
    cdlPhoto: toFileUrl(data.cdl_photo_url),
    // Medical Card
    medicalCardNumber: data.medical_card_number || '',
    dotMedicalExpiration: toDateInput(data.dot_medical_expiration),
    medicalCardPhoto: toFileUrl(data.medical_card_photo_url),
    // TWIC Card
    twicCardNumber: data.twic_card_number || '',
    twicExpiration: toDateInput(data.twic_expiration),
    twicCardPhoto: toFileUrl(data.twic_card_photo_url),
    // Port Access
    eModalId: data.emodal_id || '',
    pierPassId: data.pier_pass_id || '',
    portAccessExpiration: toDateInput(data.port_access_expiration),
    // Insurance
    insuranceProvider: data.insurance_provider || '',
    insurancePolicyNumber: data.insurance_policy_number || '',
    insuranceExpiration: toDateInput(data.insurance_expiration),
    insuranceTypes: data.insurance_types || [],
    insuranceDocument: toFileUrl(data.insurance_document_url),
    // Drug Testing
    drugTestStatus: (data.drug_test_status || 'pending') as Driver['drugTestStatus'],
    lastDrugTest: toDateInput(data.last_drug_test),
    drugTestDocument: toFileUrl(data.drug_test_document_url),
    // Safety Records
    accidents: data.accidents || 0,
    violations: data.violations || 0,
    safetyScore: data.safety_score || 100,
    lastAccidentDate: toDateInput(data.last_accident_date) || 'N/A',
    lastViolationDate: toDateInput(data.last_violation_date) || 'N/A',
    // Notes
    dispatcherNote: data.dispatcher_note || '',
    adminNote: data.admin_note || '',
    // Rating
    rating: data.rating,
    // Card display fields.
    // `current_location` is a PostGIS geography column that arrives as raw WKB
    // hex — never printable. The city resolved from the live GPS fix is what
    // the card means by "location".
    location: [data.current_city, data.current_state].filter(Boolean).join(', '),
    locationUpdatedAt: data.location_updated_at,
    activeTasks: data.active_tasks ? Number(data.active_tasks) : 0,
    todayTasks: data.today_tasks ? Number(data.today_tasks) : 0,
    hoursOnDuty: data.hours_on_duty ? Number(data.hours_on_duty) : 0,
    tasks: (data.tasks || []).map(t => ({
      id: t.id,
      taskType: t.task_type,
      status: t.status,
      appointmentTime: t.appointment_time,
    })),
    userId: data.user_id,
  };
}

function transformToBackend(data: Partial<Driver>): Partial<BackendDriver> {
  const result: Partial<BackendDriver> = {};

  if (data.name !== undefined) result.full_name = data.name;
  if (data.phone !== undefined) result.phone = data.phone;
  if (data.email !== undefined) result.email = data.email;
  if (data.avatar !== undefined) result.avatar_url = data.avatar;
  if (data.company !== undefined) result.company = data.company;
  if (data.businessName !== undefined) result.business_name = data.businessName;
  if (data.ein !== undefined) result.ein = data.ein;
  if (data.businessType !== undefined) result.business_type = data.businessType;
  if (data.businessAddress !== undefined) result.business_address = data.businessAddress;
  if (data.status !== undefined) result.status = statusToBackend[data.status];
  if (data.employmentType !== undefined) result.driver_type = data.employmentType;
  if (data.workStatus !== undefined) result.work_status = data.workStatus;
  if (data.emergencyContact?.name !== undefined) result.emergency_contact_name = data.emergencyContact.name;
  if (data.emergencyContact?.phone !== undefined) result.emergency_contact_phone = data.emergencyContact.phone;
  if (data.languagePreference !== undefined) result.language_preference = data.languagePreference;
  if (data.preferredPortYard !== undefined) result.preferred_port_yard = data.preferredPortYard;
  if (data.taskPreferences !== undefined) result.task_preferences = data.taskPreferences;
  if (data.appLoginEnabled !== undefined) result.app_login_enabled = data.appLoginEnabled;
  // CDL Information
  if (data.licenseNumber !== undefined) result.license_number = data.licenseNumber;
  if (data.licenseExpiration !== undefined) result.license_expiration = data.licenseExpiration;
  if (data.cdlClass !== undefined) result.cdl_class = data.cdlClass;
  if (data.cdlState !== undefined) result.cdl_state = data.cdlState;
  if (data.cdlIssueDate !== undefined) result.cdl_issue_date = data.cdlIssueDate;
  if (data.cdlRestrictions !== undefined) result.cdl_restrictions = data.cdlRestrictions;
  if (data.endorsements !== undefined) result.endorsements = data.endorsements;
  if (data.cdlPhoto !== undefined) result.cdl_photo_url = data.cdlPhoto || null;
  // Medical Card
  if (data.medicalCardNumber !== undefined) result.medical_card_number = data.medicalCardNumber;
  if (data.dotMedicalExpiration !== undefined) result.dot_medical_expiration = data.dotMedicalExpiration;
  if (data.medicalCardPhoto !== undefined) result.medical_card_photo_url = data.medicalCardPhoto || null;
  // TWIC Card
  if (data.twicCardNumber !== undefined) result.twic_card_number = data.twicCardNumber;
  if (data.twicExpiration !== undefined) result.twic_expiration = data.twicExpiration;
  if (data.twicCardPhoto !== undefined) result.twic_card_photo_url = data.twicCardPhoto || null;
  // Port Access
  if (data.eModalId !== undefined) result.emodal_id = data.eModalId;
  if (data.pierPassId !== undefined) result.pier_pass_id = data.pierPassId;
  if (data.portAccessExpiration !== undefined) result.port_access_expiration = data.portAccessExpiration;
  // Insurance
  if (data.insuranceProvider !== undefined) result.insurance_provider = data.insuranceProvider;
  if (data.insurancePolicyNumber !== undefined) result.insurance_policy_number = data.insurancePolicyNumber;
  if (data.insuranceExpiration !== undefined) result.insurance_expiration = data.insuranceExpiration;
  if (data.insuranceTypes !== undefined) result.insurance_types = data.insuranceTypes;
  if (data.insuranceDocument !== undefined) result.insurance_document_url = data.insuranceDocument || null;
  // Drug Testing
  if (data.drugTestStatus !== undefined) result.drug_test_status = data.drugTestStatus;
  if (data.lastDrugTest !== undefined) result.last_drug_test = data.lastDrugTest;
  if (data.drugTestDocument !== undefined) result.drug_test_document_url = data.drugTestDocument || null;
  // Safety Records
  if (data.accidents !== undefined) result.accidents = data.accidents;
  if (data.violations !== undefined) result.violations = data.violations;
  if (data.safetyScore !== undefined) result.safety_score = data.safetyScore;
  if (data.lastAccidentDate !== undefined && data.lastAccidentDate !== 'N/A') result.last_accident_date = data.lastAccidentDate;
  if (data.lastViolationDate !== undefined && data.lastViolationDate !== 'N/A') result.last_violation_date = data.lastViolationDate;
  // Notes
  if (data.dispatcherNote !== undefined) result.dispatcher_note = data.dispatcherNote;
  if (data.adminNote !== undefined) result.admin_note = data.adminNote;

  return result;
}

// ==================== API Methods ====================

export const driverApi = {
  /**
   * Get all drivers with optional filters (returns transformed frontend Driver type)
   */
  async getAll(params?: DriverListParams): Promise<{ data: Driver[]; total: number }> {
    const response = await api.get('/drivers', { params });
    const backendData = response.data as DriverListResponse;

    return {
      data: (backendData.data || []).map(transformFromBackend),
      total: backendData.total || 0,
    };
  },

  /**
   * Get all drivers with optional filters (returns raw backend data without transformation)
   * Use this when you need snake_case backend fields directly.
   */
  async getAllRaw(params?: DriverListParams): Promise<{ data: BackendDriver[]; total: number }> {
    const response = await api.get('/drivers', { params });
    const backendData = response.data as DriverListResponse;

    return {
      data: backendData.data || [],
      total: backendData.total || 0,
    };
  },

  /**
   * Get a single driver by ID
   */
  async getById(id: string): Promise<Driver> {
    const response = await api.get(`/drivers/${id}`);
    return transformFromBackend(response.data);
  },

  /**
   * Create a new driver
   */
  async create(data: Omit<Driver, 'id'>): Promise<Driver> {
    const backendData = transformToBackend(data);
    const response = await api.post('/drivers', backendData);
    return transformFromBackend(response.data);
  },

  /**
   * Update an existing driver
   */
  async update(id: string, data: Partial<Driver>): Promise<Driver> {
    const backendData = transformToBackend(data);
    const response = await api.put(`/drivers/${id}`, backendData);
    return transformFromBackend(response.data);
  },

  /**
   * Delete a driver
   */
  async delete(id: string): Promise<void> {
    await api.delete(`/drivers/${id}`);
  },

  /**
   * Get driver statistics
   */
  async getStats(): Promise<Record<string, unknown>> {
    const response = await api.get('/drivers/stats');
    return response.data;
  },

  // ==================== Notes API ====================

  async getNotes(driverId: string, noteType?: 'internal' | 'shared'): Promise<{ success: boolean; data: DriverNote[] }> {
    const params = noteType ? { type: noteType } : {};
    const response = await api.get(`/drivers/${driverId}/notes`, { params });
    return response.data;
  },

  async createNote(driverId: string, noteData: Omit<DriverNote, 'id' | 'driverId' | 'createdAt'>): Promise<{ success: boolean; data: DriverNote }> {
    const response = await api.post(`/drivers/${driverId}/notes`, noteData);
    return response.data;
  },

  async updateNote(noteId: string, noteData: Partial<DriverNote>): Promise<{ success: boolean; data: DriverNote }> {
    const response = await api.put(`/drivers/notes/${noteId}`, noteData);
    return response.data;
  },

  async deleteNote(noteId: string): Promise<{ success: boolean }> {
    const response = await api.delete(`/drivers/notes/${noteId}`);
    return response.data;
  },

  // ==================== Documents API ====================

  async getDocuments(driverId: string): Promise<{ success: boolean; data: DriverDocument[] }> {
    const response = await api.get(`/drivers/${driverId}/documents`);
    return response.data;
  },

  async createDocument(driverId: string, docData: Omit<DriverDocument, 'id' | 'driverId' | 'createdAt'>): Promise<{ success: boolean; data: DriverDocument }> {
    const response = await api.post(`/drivers/${driverId}/documents`, docData);
    return response.data;
  },

  async updateDocument(docId: string, docData: Partial<DriverDocument>): Promise<{ success: boolean; data: DriverDocument }> {
    const response = await api.put(`/drivers/documents/${docId}`, docData);
    return response.data;
  },

  async deleteDocument(docId: string): Promise<{ success: boolean }> {
    const response = await api.delete(`/drivers/documents/${docId}`);
    return response.data;
  },

  // ==================== Performance API ====================

  async getPerformance(driverId: string, days?: number): Promise<{ success: boolean; data: DriverPerformance }> {
    const params = days ? { days } : {};
    const response = await api.get(`/drivers/${driverId}/performance`, { params });
    return response.data;
  },

  async getPerformanceHistory(driverId: string): Promise<{ success: boolean; data: Record<string, unknown>[] }> {
    const response = await api.get(`/drivers/${driverId}/performance/history`);
    return response.data;
  },
};

// ==================== Driver Invite Code Types ====================

export type InviteTargetRole = 'driver' | 'dispatcher' | 'accountant' | 'docs_clerk' | 'manager' | 'boss';

export interface DriverInviteCode {
  id: number;
  code: string;
  company_id: number;
  company_name?: string;
  status: 'active' | 'used' | 'expired' | 'revoked';
  target_role?: InviteTargetRole;
  used_by_driver_id?: number;
  used_by_driver_name?: string;
  used_at?: string;
  expires_at?: string;
  created_by: number;
  created_by_name?: string;
  created_at: string;
  note?: string;
}

export interface DriverCompanyAssociation {
  id: number;
  driver_id: number;
  driver_name?: string;
  company_id: number;
  company_name?: string;
  status: 'active' | 'inactive' | 'removed';
  is_current: boolean;
  joined_at: string;
  removed_at?: string;
  removed_by?: 'driver' | 'company';
}

// ==================== Driver Invite Code API ====================

export const driverInviteCodeApi = {
  /**
   * Generate a new driver invite code (Web端 - Dispatcher/Boss)
   * Backend: {success, message, data: {...}} → interceptor extracts data → bare DriverInviteCode
   */
  async generate(data: {
    expires_in_hours?: number;
    note?: string;
    role?: InviteTargetRole;
  }): Promise<DriverInviteCode> {
    const response = await api.post('/company/driver-invite-codes', data);
    return response.data;
  },

  /**
   * Get list of invite codes for current company
   * Backend: {success, data: [...], total, limit, offset} → interceptor strips success → {data, total, limit, offset}
   */
  async getAll(params?: {
    status?: 'active' | 'used' | 'expired' | 'revoked';
    limit?: number;
    offset?: number;
  }): Promise<{ data: DriverInviteCode[]; total: number }> {
    const response = await api.get('/company/driver-invite-codes', { params });
    return response.data;
  },

  /**
   * Revoke an invite code (must be unused)
   * Backend: {success, message} → interceptor fallback → {success, message}
   */
  async revoke(id: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/company/driver-invite-codes/${id}`);
    return response.data;
  },
};

// ==================== Driver Company Association API ====================

export const driverCompanyApi = {
  /**
   * Get list of drivers associated with current company
   * Backend: {success, drivers: [...], total} (no limit) → interceptor fallback → full object
   */
  async getCompanyDrivers(params?: {
    status?: 'active' | 'inactive' | 'removed';
    limit?: number;
    offset?: number;
  }): Promise<{ drivers: DriverCompanyAssociation[]; total: number }> {
    const response = await api.get('/company/drivers', { params });
    return response.data;
  },

  /**
   * Remove a driver from company (soft delete)
   */
  async removeDriver(driverId: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/company/drivers/${driverId}`);
    return response.data;
  },
};

